import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, User, Briefcase, Home as HomeIcon, Building2, NotebookPen, Calendar } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Walkthrough Needed", "Quote Sent", "Scheduled", "Completed", "Cancelled"];

const RES_TRIGGERS = [
  "Residential cleaning", "Move-in cleaning", "Move-out cleaning",
  "Pre-listing cleaning", "Property management turnover cleaning",
  "Biweekly cleaning", "Monthly cleaning",
];
const COM_TRIGGERS = ["Commercial cleaning", "Office cleaning"];

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function hasValue(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/** A single row. Hides itself when value is empty (unless required=true → "Not provided"). */
function Row({ label, value, required = false, testid }) {
  const present = hasValue(value);
  if (!present && !required) return null;
  return (
    <div data-testid={testid} className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-2.5 border-b border-slate-100 last:border-0">
      <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold sm:col-span-1">{label}</dt>
      <dd className="sm:col-span-2 text-sm text-[#0F172A]">
        {!present ? (
          <span className="text-slate-400 italic">Not provided</span>
        ) : Array.isArray(value) ? (
          <div className="flex flex-wrap gap-1.5">
            {value.map((v) => (
              <span key={String(v)} className="px-2.5 py-0.5 rounded-full bg-slate-100 text-[12px] font-medium">{v}</span>
            ))}
          </div>
        ) : value}
      </dd>
    </div>
  );
}

/** Wrapped section. Renders nothing if no rows have data (when `keepEmpty` is false). */
function Section({ icon: Icon, title, children, testid }) {
  return (
    <section data-testid={testid} className="bg-white rounded-2xl border border-slate-100 p-6">
      <header className="flex items-center gap-2 mb-2">
        <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1E3A8A]/10 to-[#A78BFA]/10 flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#1E3A8A]" />
        </span>
        <h3 className="font-display font-semibold text-[#0F172A]">{title}</h3>
      </header>
      <dl className="divide-y divide-slate-100">{children}</dl>
    </section>
  );
}

export default function AdminRequestDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/estimates/${id}`);
      setItem(data);
      setStatus(data.status);
      setNotes(data.internal_notes || "");
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/estimates/${id}`, { status, internal_notes: notes });
      toast.success("Saved");
      load();
    } catch { toast.error("Save failed"); }
    setBusy(false);
  };

  if (!item) return <div className="text-sm text-[#475569]">Loading…</div>;

  const services = item.services_needed || [];
  const segment = item.residential_or_commercial || "";
  const hasRes =
    segment === "Residential" || segment === "Both" ||
    services.some((s) => RES_TRIGGERS.includes(s));
  const hasCom =
    segment === "Commercial" || segment === "Both" ||
    services.some((s) => COM_TRIGGERS.includes(s));

  return (
    <div data-testid={`admin-request-detail-${id}`}>
      <Link to="/admin/requests" className="inline-flex items-center gap-1.5 text-sm text-[#1E3A8A] hover:underline" data-testid="admin-request-back">
        <ArrowLeft className="w-4 h-4" /> Back to requests
      </Link>
      <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-display font-bold text-[#0F172A]">{item.full_name}</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Submitted {fmtDateTime(item.created_at)}
            {segment && <> · <span className="font-semibold">{segment}</span></>}
          </p>
        </div>
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">{item.status}</span>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-5">
        {/* Left: content sections */}
        <div className="lg:col-span-2 space-y-5">
          {/* Customer info */}
          <Section icon={User} title="Customer Info" testid="section-customer">
            <Row label="Full name" value={item.full_name} required testid="row-full-name" />
            <Row label="Phone" value={item.phone} required testid="row-phone" />
            <Row label="Email" value={item.email} required testid="row-email" />
            <Row label="City / area" value={item.city_area} testid="row-city" />
            <Row label="Submitted" value={fmtDateTime(item.created_at)} testid="row-submitted" />
          </Section>

          {/* Services Requested */}
          <Section icon={Briefcase} title="Services Requested" testid="section-services">
            <Row label="Services needed" value={item.services_needed} required testid="row-services" />
            <Row label="Add-ons requested" value={item.add_ons_requested} testid="row-addons" />
            <Row label="Special request notes" value={item.special_notes} testid="row-special-notes" />
          </Section>

          {/* Property / Job info */}
          <Section icon={Calendar} title="Property / Job Info" testid="section-property">
            <Row label="Property address" value={item.property_address} testid="row-address" />
            <Row label="Preferred date" value={item.preferred_date} testid="row-preferred-date" />
            <Row label="Residential or commercial" value={segment} testid="row-segment" />
            <Row label="Access method" value={item.access_method} testid="row-access" />
          </Section>

          {/* Residential details — only when applicable */}
          {hasRes && (
            <Section icon={HomeIcon} title="Residential Details" testid="section-residential">
              <Row label="Square footage" value={item.square_footage} testid="row-res-sqft" />
              <Row label="Bedrooms" value={item.bedrooms} testid="row-res-bedrooms" />
              <Row label="Bathrooms" value={item.bathrooms} testid="row-res-bathrooms" />
              <Row label="Vacant or occupied" value={item.vacant_or_occupied} testid="row-res-occupancy" />
              <Row label="Property condition" value={item.property_condition} testid="row-res-condition" />
              <Row label="Add-ons" value={item.add_ons_requested} testid="row-res-addons" />
            </Section>
          )}

          {/* Commercial details — only when applicable */}
          {hasCom && (
            <Section icon={Building2} title="Commercial Details" testid="section-commercial">
              <Row label="Business name" value={item.business_name} testid="row-com-business-name" />
              <Row label="Business type" value={item.business_type} testid="row-com-business-type" />
              <Row label="Approximate square footage" value={item.square_footage} testid="row-com-sqft" />
              <Row label="Number of restrooms" value={item.number_of_restrooms} testid="row-com-restrooms" />
              <Row label="Cleaning frequency" value={item.cleaning_frequency} testid="row-com-frequency" />
              <Row label="Preferred cleaning time" value={item.preferred_cleaning_time} testid="row-com-time" />
              <Row label="Areas to clean" value={item.areas_to_clean} testid="row-com-areas" />
              <Row label="Walkthrough requested" value={item.walkthrough_requested} testid="row-com-walkthrough" />
            </Section>
          )}
        </div>

        {/* Right: admin management */}
        <div className="space-y-5">
          <Section icon={NotebookPen} title="Admin Management" testid="section-admin">
            <div className="py-3">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="admin-request-status"
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
              >
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>

              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mt-4 mb-1.5">Internal notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="admin-request-notes"
                rows="6"
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
                placeholder="Private notes — only admins can see this"
              />

              <button
                onClick={save}
                disabled={busy}
                data-testid="admin-request-save"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors disabled:opacity-50"
              >
                {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </Section>

          <div className="bg-white rounded-2xl border border-slate-100 p-5 text-xs text-[#475569]" data-testid="section-meta">
            <div>Email notification sent: <strong className="text-[#0F172A]">{String(item.email_notification_sent)}</strong></div>
            {item.email_notification_error && (
              <div className="mt-1.5 text-amber-700">{item.email_notification_error}</div>
            )}
            {item.updated_at && (
              <div className="mt-1.5 text-slate-500">Last updated {fmtDateTime(item.updated_at)}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
